(ns arthur.domain.leaf
  "Leaf addressing for tier 1: the document as a map of PATH -> value.

  This is the shape docs/architecture.md's sync design needs, built now so that
  there is nothing to retrofit later. Multiplayer is out of this step's scope and
  the addressing is not, because the addressing is the part that cannot be added
  afterwards: it decides what a write is, and therefore what two people can do at
  once.

      clip/<cid>/name                     a label
      clip/<cid>/timing                   fps
      clip/<cid>/stage                    width, height
      clip/<cid>/palette/<pid>            a named indexed palette asset
      clip/<cid>/palette-default          the project fallback palette id
      clip/<cid>/root                     the symbol the document opens on
      clip/<cid>/source                   the analysis record this came out of
      clip/<cid>/subject/<subj>           a tracked subject and its params
      clip/<cid>/feature/<fid>            one feature: area, nodes, params
      clip/<cid>/group/<gid>              an eye pair and its shared params
      clip/<cid>/symbol/<sid>                     frame count, optional palette
      clip/<cid>/symbol/<sid>/node/<nid>          kind, parent, stencil, z, time
      clip/<cid>/symbol/<sid>/channel/<nid>/<prop>
      clip/<cid>/symbol/<sid>/measured/<nid>      the channels a re-freeze owns

  WHY NODES SIT UNDER A SYMBOL. A clip holds a library of symbols and each has
  its own nodes, so the symbol id is a path segment. No symbol has a reserved
  segment: `main` in a path is an id like any other.

  The timing leaf holds the document's one rate. A symbol leaf holds its frame
  count and no rate: a symbol is a bag of frames.

  WHY THESE BOUNDARIES. Last-writer-wins only clobbers when its unit is too big,
  so the cut is chosen so that the things people do simultaneously land on
  different leaves. Every node has its own leaf, because two people adding nodes
  would otherwise collide always. Every channel has its own, because keying the
  mouth and keying a brow are the same size of edit as each other and nothing
  like the same edit. With fractional `:z` there is no separate draw-order leaf to
  contend on, which is the second thing fractional indices buy.

  WHY PARAMS ARE NOT SPLIT BY AREA. docs/architecture.md's list has
  `clip/:cid/params/:area`, from a draft where params were one blob per clip and
  two people tuning teeth and eyes collided on every slider move. Step 8 moved
  settings onto the subject, the feature and the group, and a FEATURE HAS EXACTLY
  ONE AREA — so the feature leaf already is the area-scoped leaf, and splitting it
  again would only separate a feature's params from the feature's identity.

  WHY `measured` IS ONE LEAF AND CHANNELS ARE NOT. `:head`'s measured channels are
  not authored: they are written together by a freeze and replaced together by a
  re-freeze, and `head-mode` exposes them through `:channels`. The same is true
  of a face's `:plate`, whose measured channels register its footage. Which
  measured frame they read is the node's `:reads` and `:time :holds`, which
  save with the node. A leaf per measured
  channel would offer a write nobody can make. The authored channels beside them
  are one leaf each, because a hand writes one at a time.

  A LEAF PATH IS \"/\"-DELIMITED and an id is one segment of it, so a namespaced id
  — docs/architecture.md draws one as `:eye-r/iris` — is written `eye-r~iris`.
  `~` is then refused inside a name, which is the whole of the escaping and is why
  it is one character rather than a scheme."
  (:require [arthur.domain.clip :as clip]
            [arthur.domain.sha256 :as sha]
            [arthur.domain.symbol :as symbol]
            [clojure.string :as str]))

;; ---------------------------------------------------------------------------
;; ids and paths

(defn segment
  "An id -> one path segment."
  [id]
  (let [s (if (keyword? id) (subs (str id) 1) (str id))]
    (when (str/includes? s "~")
      (throw (ex-info "an id cannot contain ~: it is the namespace separator inside a leaf path"
                      {:id id})))
    (str/replace s "/" "~")))

(def ^:private uuid-segment
  "Canonical UUID form: 8-4-4-4-12 hex digits, and nothing else.

  A PLACEMENT'S ID IS A UUID — see `demo/stage/compose` for why — and a leaf path
  is text, so reading one back has to decide which ids are uuids and which are
  keywords. It decides by SHAPE, which is a judgement worth stating: a keyword
  that happened to be thirty-six characters of hex in exactly this grouping would
  come back a uuid. Nothing names a node that by hand, and the alternative — a
  sigil on every segment — would change the shape of every path in every leaf to
  disambiguate a case that does not arise. `^` and `$` are the load-bearing part;
  without them a longer id CONTAINING a uuid would match."
  #"^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$")

(defn unsegment
  "One path segment -> the id it names: a uuid when it is shaped like one, a
  keyword otherwise."
  [s]
  (let [s (str/replace s "~" "/")]
    (if (re-find uuid-segment s)
      (uuid s)
      (keyword s))))

(defn- prop->path
  "A channel's property vector -> one path segment. `[:geom :pts]` is \"geom.pts\"
  and `[:vis]` is \"vis\"."
  [prop]
  (let [parts (map #(subs (str %) 1) prop)]
    (doseq [p parts]
      (when (or (str/includes? p ".") (str/includes? p "/"))
        (throw (ex-info "a channel property cannot contain . or /: both are path punctuation"
                        {:prop prop}))))
    (str/join "." parts)))

(defn- path->prop [s]
  (mapv keyword (str/split s #"\.")))

;; ---------------------------------------------------------------------------
;; the split

(def ^:private node-channel-keys #{:channels :measured})

(defn leaves
  "One clip -> path -> value.

  A leaf whose value would be empty is OMITTED rather than written as `{}`, and
  that is what makes the round trip exact: the demo clip has no `:fps` and its root
  node has no `:channels`, and a codec that invented them would hand back a clip
  that is not `=` to the one it was given."
  [cid clip]
  (let [unknown (remove clip/clip-keys (keys clip))]
    (when (seq unknown)
      (throw (ex-info "the clip has a field with no leaf to save it in; see arthur.domain.clip/clip-keys"
                      {:unknown (vec (sort-by str unknown))}))))
  (doseq [[id sym] (:symbols clip)]
    (let [unknown (remove symbol/symbol-keys (keys sym))]
      (when (seq unknown)
        (throw (ex-info "a symbol has a field with no leaf to save it in; see arthur.domain.symbol/symbol-keys"
                        {:symbol id :unknown (vec (sort-by str unknown))})))))
  (let [at   (fn [& parts] (str/join "/" (into ["clip" (segment cid)] parts)))
        some-leaf (fn [path v] (when (seq v) {path v}))]
    (apply merge
           (some-leaf (at "name")   (select-keys clip [:name]))
           (some-leaf (at "timing") (select-keys clip [:fps]))
           (some-leaf (at "stage")  (select-keys clip [:width :height]))
           (some-leaf (at "palette-default") (select-keys clip [:default-palette]))
           (some-leaf (at "root") (select-keys clip [:root]))
           (some-leaf (at "analyses") (:analyses clip))
           (concat
            (for [[id v] (:subjects clip)] {(at "subject" (segment id)) v})
            (for [[id v] (:features clip)] {(at "feature" (segment id)) v})
            (for [[id v] (:groups clip)]   {(at "group" (segment id)) v})
            (for [[id v] (:palettes clip)] {(at "palette" (segment id)) v})
            ;; The symbol's own facts. `:id` is the path segment, so writing it
            ;; into the value as well would be the one field a rename could
            ;; disagree with itself about; `clip` puts it back.
            (for [[sid sym] (:symbols clip)]
              {(at "symbol" (segment sid))
               (select-keys sym [:name :frames :width :height :palette
                                 :palette-track :palette-channel :type :palette-ref :display
                                 :media])})
            (for [[sid sym] (:symbols clip)
                  [id n]   (:nodes sym)]
              {(at "symbol" (segment sid) "node" (segment id))
               (apply dissoc n node-channel-keys)})
            (for [[sid sym] (:symbols clip)
                  [id n]   (:nodes sym)
                  :when    (seq (:measured n))]
              {(at "symbol" (segment sid) "measured" (segment id)) (:measured n)})
            (for [[sid sym] (:symbols clip)
                  [id n]   (:nodes sym)
                  [prop ch] (:channels n)]
              {(at "symbol" (segment sid) "channel" (segment id) (prop->path prop)) ch})))))

(defn clip
  "The inverse of `leaves`, for one clip. Paths belonging to another clip are
  ignored, so a project's whole leaf map can be handed straight in.

  A symbol's `:id` is restored from its path segment rather than read out of the
  value, which is why `leaves` does not write it: a segment and a field that both
  claim to be the id are two places for one fact."
  [cid leaves]
  (let [want (segment cid)]
    (reduce
     (fn [acc [path v]]
       (let [[_ found kind a b c] (str/split path #"/")]
         (if-not (= want found)
           acc
           (if (= "symbol" kind)
             (let [sid (unsegment a)
                   acc (assoc-in acc [:symbols sid :id] sid)]
               (case b
                 ;; `:nodes` is there before any node leaf is: an empty
                 ;; symbol has none, and is still a symbol.
                 nil        (update-in acc [:symbols sid] #(merge {:nodes {}} % v))
                 "node"     (update-in acc [:symbols sid :nodes (unsegment c)] merge v)
                 "measured" (assoc-in acc [:symbols sid :nodes (unsegment c) :measured] v)
                 "channel"  (assoc-in acc [:symbols sid :nodes (unsegment c)
                                           :channels (path->prop (nth (str/split path #"/") 6))]
                                      v)
                 (throw (ex-info "not a leaf path" {:path path}))))
             (case kind
               "name"     (merge acc v)
               "timing"   (merge acc v)
               "stage"    (merge acc v)
               "analyses" (assoc acc :analyses v)
               "palette-default" (merge acc v)
               "root"     (merge acc v)
               "palette"  (assoc-in acc [:palettes (unsegment a)] v)
               "subject"  (assoc-in acc [:subjects (unsegment a)] v)
               "feature"  (assoc-in acc [:features (unsegment a)] v)
               "group"    (assoc-in acc [:groups (unsegment a)] v)
               (throw (ex-info "not a leaf path" {:path path})))))))
     {}
     ;; Sorted, so `node` lands before `channel` and `measured` under one id and
     ;; the node map is merged INTO rather than over. `update-in ... merge` makes
     ;; the order not matter; sorting makes it not matter for a reason.
     (sort-by key leaves))))

;; ---------------------------------------------------------------------------

(defn problems
  "Human-readable reasons this leaf map is not a document. Empty means it is one.

  The dense check is the tier discipline, stated where a save can enforce it: a
  document that named `\"take/geom\"` would be a document that only means anything
  on the machine that produced it, and the whole point of tier 2 being
  content-addressed is that it does not have to travel with tier 1 to be found."
  [leaves]
  (let [parts (into {} (map (juxt identity #(vec (str/split % #"/")))) (keys leaves))
        ;; A node leaf, by (clip, symbol, node). Under a symbol id, because two
        ;; symbols may both hold a `:mouth` and a channel of one is not a channel
        ;; of the other.
        nodes (into #{} (keep (fn [[_ p]]
                                (when (and (= 6 (count p)) (= "symbol" (nth p 2))
                                           (= "node" (nth p 4)))
                                  [(nth p 1) (nth p 3) (nth p 5)])))
                    parts)
        ;; Which segment index holds the kind, and what shapes are legal.
        legal? (fn [p]
                 (and (= "clip" (first p)) (second p)
                      (if (= "symbol" (nth p 2 nil))
                        (case (count p)
                          4 true                                   ; the symbol itself
                          6 (#{"node" "measured"} (nth p 4))
                          7 (= "channel" (nth p 4))
                          false)
                        (case (count p)
                          ;; The clip's own facts carry no id.
                          3 (#{"name" "timing" "stage" "analyses" "palette-default" "root"} (nth p 2))
                          4 (#{"subject" "feature" "group" "palette"} (nth p 2))
                          false))))]
    (vec
     (concat
      (for [[path p] (sort-by key parts)
            :when    (not (legal? p))]
        (str (pr-str path) " is not a leaf path"))
      (for [[path p] (sort-by key parts)
            :when    (and (legal? p) (= "symbol" (nth p 2 nil)) (>= (count p) 6)
                          (#{"channel" "measured"} (nth p 4))
                          (not (contains? nodes [(nth p 1) (nth p 3) (nth p 5)])))]
        (str (pr-str path) " addresses a node with no node leaf"))
      (for [[path p] (sort-by key parts)
            :let     [v (get leaves path)]
            :when    (and (legal? p) (= "symbol" (nth p 2 nil)) (= 7 (count p))
                          (:dense v) (not (sha/key? (:store (:dense v)))))]
        (str (pr-str path) " names tier 2 as " (pr-str (:store (:dense v)))
             " — a dense channel in a saved document names a content address"))))))
